import {FunctionComponent, PropsWithChildren, useContext, useEffect, useState} from 'react';
import {AppContext} from '@/App';
import Button from '@showclix-shared/Button';

interface EmbeddedCheckoutInstance {
    setOnLogout: (callback: () => void) => void;
    refetchReservationAndLogin: (token: string, secret: string, member: object) => void;
}

interface OneIDProps {
    clientId: string;
    environment: string;
    listingId: string;
    member_button: string;
    onMembershipLoginChange: (isLoggedIn: boolean) => void;
    onVerificationSent: () => void;
    onError: (message: string) => void;
    embeddedCheckout: EmbeddedCheckoutInstance | null;
}

export const DisneyOneID: FunctionComponent<PropsWithChildren<OneIDProps>> = ({
  clientId,
  environment,
  member_button,
  listingId,
  onMembershipLoginChange,
  onVerificationSent,
  onError,
  embeddedCheckout
}) => {

    const { t } = useContext(AppContext);
    const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
    const [oneID, setOneID] = useState<any>(null);
    const [isReady, setIsReady] = useState<boolean>(false);

    const ONE_ID_CONFIG = {
        clientId,
        environment,
        listingId,
        responderPage: new URL(
            `/js/membership/disney/responder${environment === 'STAGE' ? '-stg' : ''}.html`, window.location.origin
        ).toString(),
    };

    const onOneIDInit = (init: any) => {
        if (init.loggedIn) {
            oneID.getGuest(false).then(onOneIDLogin);
        }
        setIsReady(true);
    };

    const onOneIDLogin = (guest: any) => {
        if (guest) {
            createNewReservation(guest);
        }
    };

    const onOneIDLogout = () => {
        setIsLoggedIn(false);
        onMembershipLoginChange(false);
    };

    const onOneIDError = () => {
    };

    const doLogin = () => {
        oneID.launchLogin();
    };

    const doLogout = () => {
        if (!oneID) {
            return;
        }
        oneID.logout();
    };

    useEffect(() => {
        const script = document.createElement('script');
        script.src =
            environment == 'STAGE'
                ? 'https://stg.cdn.registerdisney.go.com/v4/OneID.js'
                : 'https://cdn.registerdisney.go.com/v4/OneID.js';
        script.async = true;
        script.onload = () => {
            try {
                const disneyOneID = window.OneID.get(ONE_ID_CONFIG);
                setOneID(disneyOneID);
                disneyOneID.init();
            } catch (e) {
                // If already initialized
                const disneyOneID = window.OneID.get();
                if (disneyOneID) {
                    setOneID(disneyOneID);
                    setIsReady(true);
                }
            }
        };
        document.body.appendChild(script);
    }, []);

    useEffect(() => {
        if (oneID !== null) {
            oneID.on('init', onOneIDInit);
            oneID.on('login', onOneIDLogin);
            oneID.on('logout', onOneIDLogout);
            oneID.on('error', onOneIDError);
        }
    }, [oneID]);

    useEffect(() => {
        if (!embeddedCheckout || !oneID) {
            return;
        }
        embeddedCheckout.setOnLogout(doLogout);
    }, [embeddedCheckout, doLogout, oneID]);

    const createNewReservation = (guest: any) => {
        // Get data and send request
        setIsReady(false);
        const returnUrl = window.location.href;
        const membershipData = {
            returnUrl,
            listingId,
            membership: {
                submit_member: 'submit_member',
                swid: guest.profile.swid,
                disney_token: guest.token.access_token,
                id_token: guest.token.access_token,
            },
        };

        fetch('/api/memberships/', {
            method: 'POST',
            mode: 'cors',
            body: JSON.stringify(membershipData),
        })
            .then((res) => res.json())
            .then((data) => {
                if (data.verify) {
                    onVerificationSent();
                    return;
                }
                if (data.success) {
                    embeddedCheckout.refetchReservationAndLogin(data.token, data.secret, data.member);
                    onMembershipLoginChange(true);
                    setIsLoggedIn(true);
                    setIsReady(true);
                    return;
                }

                const errorMessage =
                    data?.error === 'no_membership'
                        ? t('membership__disney_login_without_membership')
                        : data?.error || t('membership__an_error_occurred_try_again');
                onError(errorMessage);
            })
            .catch(() => {
                onError(t('membership__an_error_occurred_try_again'));
            });
    };

    return (
        <>
            {!isLoggedIn && (
                <Button onClick={doLogin} loading={!isReady} disabled={!isReady} variant="button--primary">
                    {member_button}
                </Button>
            )}
            {isLoggedIn && (
                <Button onClick={doLogout} variant="button--primary">
                    Logout
                </Button>
            )}
        </>
    );
};

export default DisneyOneID;
